import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
      toast.success('Reset link sent if email exists');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout" data-testid="forgot-password-page">
      <div className="auth-left">
        <div className="auth-form animate-slide-up">
          <Link
            to="/"
            className="group flex items-center gap-3 mb-8 -ml-1 px-1 py-1 rounded-lg hover:bg-slate-100/60 transition-colors"
            aria-label="Back to home"
            data-testid="forgot-home-link"
          >
            <img src="/logo-login.png" alt="ClinicalFlow" className="w-12 h-12 rounded-xl object-contain" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] group-hover:text-[#1e3a5f] transition-colors">ClinicalFlow</h1>
              <p className="text-sm text-slate-500">Research Request Portal</p>
            </div>
          </Link>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-['Manrope']">
                {submitted ? 'Check your email' : 'Forgot password?'}
              </CardTitle>
              <CardDescription>
                {submitted 
                  ? 'We sent you a password reset link'
                  : 'Enter your email and we\'ll send you a reset link'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                  </div>
                  <p className="text-center text-slate-600">
                    If an account exists for <strong>{email}</strong>, you will receive a password reset link shortly.
                  </p>
                  <p className="text-center text-sm text-slate-500">
                    The link will expire in 1 hour.
                  </p>
                  <div className="pt-4">
                    <Link to="/login">
                      <Button variant="outline" className="w-full" data-testid="back-to-login-btn">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to login
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm animate-slide-down">
                      <AlertCircle size={16} />
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="name@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-blue-900 hover:bg-blue-950"
                    disabled={loading}
                    data-testid="send-reset-btn"
                  >
                    {loading ? 'Sending...' : 'Send reset link'}
                  </Button>

                  <div className="text-center pt-4">
                    <Link 
                      to="/login" 
                      className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center"
                      data-testid="back-link"
                    >
                      <ArrowLeft className="mr-1 h-4 w-4" />
                      Back to login
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="auth-right">
        <div className="relative z-10 text-center text-white p-12 max-w-lg">
          <h2 className="text-3xl font-bold mb-4 font-['Manrope']">
            Secure Password Recovery
          </h2>
          <p className="text-lg text-blue-100 opacity-90">
            We'll send you a secure link to reset your password. The link expires after 1 hour for your security.
          </p>
        </div>
      </div>
    </div>
  );
}
